import React, { ChangeEvent, FC, useCallback, useEffect } from 'react';
import { UseFormReturn } from 'react-hook-form';
import Grid from '@mui/material/Grid';
import { useTranslation } from 'react-i18next';
import { TextInputController } from '../InputControllers/TextInputController';
import { SelectController } from '../InputControllers/SelectController';
import { IBasicOrganization } from '../../resources/models/IOrganization';
import Switch from '@mui/material/Switch';
import Box from '@mui/material/Box';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import { countries } from '../../helpers/countries';
import { TEditCurrentOrganizationFormType } from './EditCurrentOrganizationDialog';

const countryItems = countries.map((country) => ({
  label: country.name,
  value: country.code,
}));

interface IProps {
  organization?: IBasicOrganization;
  onSubmit: (fields: TEditCurrentOrganizationFormType) => void;
  form: UseFormReturn<TEditCurrentOrganizationFormType>;
}

export const EditCurrentOrganizationForm: FC<IProps> = ({
  organization,
  onSubmit,
  form: {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  },
}) => {
  const { t } = useTranslation();

  const address = watch('address');
  const sameBillingAddress = watch('same_billing_address');

  useEffect(() => {
    if (sameBillingAddress && address) {
      setValue('billing_address', address);
    }
  }, [sameBillingAddress, address, setValue]);

  const onChangeSameBillingAddress = useCallback(
    (event: ChangeEvent, value: boolean) => {
      setValue('same_billing_address', value);
    },
    [setValue]
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <TextInputController
            control={control}
            errors={errors}
            defaultValue={organization?.address ?? ''}
            label={t('common.formLabel.address')}
            rules={{ required: true }}
            name="address"
          />
        </Grid>
        <Grid item xs={4} lg={4}>
          <FormControl fullWidth={true}>
            <InputLabel focused={false} shrink={true} variant="filled">
              Same billing address
            </InputLabel>
            <Box pt={2}>
              <Switch checked={sameBillingAddress} onChange={onChangeSameBillingAddress} />
            </Box>
          </FormControl>
        </Grid>
        <Grid item xs={8} lg={8}>
          <TextInputController
            control={control}
            errors={errors}
            InputProps={{
              readOnly: sameBillingAddress,
              disabled: sameBillingAddress,
            }}
            defaultValue={organization?.billing_address ?? ''}
            label={t('editOrganizationDialog.formLabel.billingAddress')}
            rules={{ required: !sameBillingAddress }}
            name={'billing_address'}
          />
        </Grid>
        <Grid item xs={12}>
          <TextInputController
            control={control}
            errors={errors}
            defaultValue={organization?.phone_number ?? ''}
            label={t('common.formLabel.phoneNumber')}
            name="phone_number"
          />
        </Grid>
        <Grid item xs={12}>
          <SelectController
            control={control}
            errors={errors}
            items={countryItems}
            label={t('editOrganizationDialog.formLabel.country')}
            name={'country'}
            defaultValue={(organization as any)?.country ?? ''}
          />
        </Grid>
      </Grid>
    </form>
  );
};
